/**
 * Category Performance — Enhanced with filters, monthly trend chart, and AI analysis
 */
import React, { useState, useRef, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Chip,
} from '@mui/material';
import {
  Psychology as AIIcon,
  Warning as WarningIcon,
  Lightbulb as LightbulbIcon,
  PlaylistAddCheck as ActionIcon,
  CameraAlt as SnapshotIcon,
  LocalOffer as BrandIcon,
  Category as CategoryIcon,
  Place as GeoIcon,
  Storefront as MixIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import api from '../../services/api';
import ExportMenu from '../Common/ExportMenu';
import UniversalFilters from '../common/UniversalFilters';
import { useCurrencyFormat } from '../../hooks/useCurrencyFormat';

const CATEGORY_COLORS = [
  '#722F37', // Burgundy
  '#B76E79', // Rose Gold
  '#C9A96E', // Champagne Gold
  '#8B7355', // Warm Taupe
  '#8B6F8B', // Mauve
  '#DEB5A0', // Blush
  '#6B8E7B', // Sage
  '#9B7653', // Camel
];

interface DimensionalInsights {
  brand_insights: string[];
  category_insights: string[];
  geographic_insights: string[];
  mix_insights: string[];
}

interface CategoryPerfResponse {
  metadata: any;
  data: CategoryRow[];
  monthly_trends: any[];
  categories: string[];
  brand_breakdown?: any[];
  category_breakdown?: any[];
  city_breakdown?: any[];
  store_type_breakdown?: any[];
  ai_analysis: {
    snapshot: string;
    risks: string[];
    opportunities: string[];
    action_plan: string[];
    dimensional_insights?: DimensionalInsights;
  };
}

interface CategoryRow {
  category: string;
  product_count: number;
  total_units_sold: number;
  total_revenue: number;
  total_cogs: number;
  gross_margin_pct: number;
  avg_transaction_value: number;
  revenue_share_percent: number;
}

const CategoryPerformance: React.FC = () => {
  // Universal filters state
  const [filters, setFilters] = useState<{
    brands: string[];
    stores: string[];
    categories: string[];
    cities: string[];
    abcClassifications: string[];
  }>({
    brands: [],
    stores: [],
    categories: [],
    cities: [],
    abcClassifications: [],
  });

  const { data: perfData, isLoading, isError, error } = useQuery<CategoryPerfResponse>({
    queryKey: ['categoryPerf', filters],
    queryFn: async () => {
      const response = await api.getCategoryPerformance({
        start_date: '2025-01-01',
        end_date: new Date().toISOString().split('T')[0],
        brands: filters.brands.length > 0 ? filters.brands.join(',') : undefined,
        stores: filters.stores.length > 0 ? filters.stores.join(',') : undefined,
        cities: filters.cities.length > 0 ? filters.cities.join(',') : undefined,
        abc_classifications: filters.abcClassifications.length > 0 ? filters.abcClassifications.join(',') : undefined,
      });
      return response.data;
    },
  });

  const categoryData = perfData?.data || [];
  const monthlyTrends = perfData?.monthly_trends || [];
  const categories = perfData?.categories || [];
  const aiAnalysis = perfData?.ai_analysis;
  const dimensional = aiAnalysis?.dimensional_insights;

  const { fmtCurrencyCompact: fmtCurrency, fmtCurrencyLabel, fmtCurrency: fmtCurrencyFull } = useCurrencyFormat();
  const chartContainerRef = useRef<HTMLDivElement>(null);

  // Render `**bold**` segments coming from the backend insight strings.
  const renderRich = (text: string): React.ReactNode => {
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((p, i) =>
      p.startsWith('**') && p.endsWith('**') ? (
        <strong key={i} style={{ color: 'var(--color-text-primary)', fontWeight: 700 }}>
          {p.slice(2, -2)}
        </strong>
      ) : (
        <React.Fragment key={i}>{p}</React.Fragment>
      )
    );
  };

  const exportRows = useMemo(() => {
    if (!categoryData.length) return [];
    const revLabel = `Revenue ${fmtCurrencyLabel()}`;
    const cogsLabel = `COGS ${fmtCurrencyLabel()}`;
    const avgTxnLabel = `Avg Transaction Value ${fmtCurrencyLabel()}`;
    const rows: Record<string, any>[] = categoryData.map((c) => ({
      Category: c.category,
      Products: c.product_count,
      'Units Sold': c.total_units_sold,
      [revLabel]: Math.round(c.total_revenue),
      [cogsLabel]: Math.round(c.total_cogs),
      'Gross Margin %': Number(c.gross_margin_pct.toFixed(1)),
      [avgTxnLabel]: Math.round(c.avg_transaction_value),
      'Revenue Share %': Number(c.revenue_share_percent.toFixed(1)),
    }));
    // Add total row
    const totRev = categoryData.reduce((a, c) => a + c.total_revenue, 0);
    const totCogs = categoryData.reduce((a, c) => a + c.total_cogs, 0);
    rows.push({
      Category: 'TOTAL',
      Products: categoryData.reduce((a, c) => a + c.product_count, 0),
      'Units Sold': categoryData.reduce((a, c) => a + c.total_units_sold, 0),
      [revLabel]: Math.round(totRev),
      [cogsLabel]: Math.round(totCogs),
      'Gross Margin %': totRev > 0 ? Number(((totRev - totCogs) / totRev * 100).toFixed(1)) : 0,
      [avgTxnLabel]: 0,
      'Revenue Share %': 100,
    });
    return rows;
  }, [categoryData, fmtCurrencyLabel]);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography sx={{ fontSize: 'var(--text-base)', color: 'var(--color-text-muted)' }}>
          Sales, revenue share, margins, and monthly evolution by product category
        </Typography>
        <ExportMenu
          data={exportRows}
          filename="Category_Performance"
          title="Category Performance"
          sheetName="Categories"
          chartRef={chartContainerRef}
        />
      </Box>

      {/* Universal Filters */}
      <UniversalFilters
        brands={filters.brands}
        stores={filters.stores}
        categories={filters.categories}
        cities={filters.cities}
        abcClassifications={filters.abcClassifications}
        onBrandsChange={(brands) => setFilters({ ...filters, brands })}
        onStoresChange={(stores) => setFilters({ ...filters, stores })}
        onCategoriesChange={(categories) => setFilters({ ...filters, categories })}
        onCitiesChange={(cities) => setFilters({ ...filters, cities })}
        onAbcClassificationsChange={(abcClassifications) => setFilters({ ...filters, abcClassifications })}
        onClearAll={() => setFilters({ brands: [], stores: [], categories: [], cities: [], abcClassifications: [] })}
        showCountries={false}
        showRetailers={false}
        showBrands={true}
        showStores={true}
        showCategories={true}
        showCities={true}
        showAbcClassifications={true}
      />

      {isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {(error as any)?.response?.data?.detail || (error as Error)?.message || 'Failed to load category performance data.'}
        </Alert>
      )}

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : categoryData.length > 0 ? (
        <>
          {/* Summary KPI Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} sm={3}>
              <Box sx={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', p: 2, textAlign: 'center' }}>
                <Typography sx={{ fontSize: 'var(--text-xs)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--color-text-muted)', fontWeight: 600 }}>
                  Total Revenue
                </Typography>
                <Typography sx={{ fontSize: 'var(--text-2xl)', fontWeight: 500, letterSpacing: 'var(--tracking-kpi)', color: 'var(--color-text-primary)', mt: 0.5, fontVariantNumeric: 'tabular-nums' }}>
                  {fmtCurrency(categoryData.reduce((s, c) => s + c.total_revenue, 0))}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Box sx={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', p: 2, textAlign: 'center' }}>
                <Typography sx={{ fontSize: 'var(--text-xs)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--color-text-muted)', fontWeight: 600 }}>
                  Total Units
                </Typography>
                <Typography sx={{ fontSize: 'var(--text-2xl)', fontWeight: 500, letterSpacing: 'var(--tracking-kpi)', color: 'var(--color-text-primary)', mt: 0.5, fontVariantNumeric: 'tabular-nums' }}>
                  {categoryData.reduce((s, c) => s + c.total_units_sold, 0).toLocaleString()}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Box sx={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', p: 2, textAlign: 'center' }}>
                <Typography sx={{ fontSize: 'var(--text-xs)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--color-text-muted)', fontWeight: 600 }}>
                  Avg Gross Margin
                </Typography>
                <Typography sx={{ fontSize: 'var(--text-2xl)', fontWeight: 500, letterSpacing: 'var(--tracking-kpi)', color: '#722F37', mt: 0.5, fontVariantNumeric: 'tabular-nums' }}>
                  {(categoryData.reduce((s, c) => s + c.gross_margin_pct, 0) / categoryData.length).toFixed(1)}%
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Box sx={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', p: 2, textAlign: 'center' }}>
                <Typography sx={{ fontSize: 'var(--text-xs)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--color-text-muted)', fontWeight: 600 }}>
                  Categories
                </Typography>
                <Typography sx={{ fontSize: 'var(--text-2xl)', fontWeight: 500, letterSpacing: 'var(--tracking-kpi)', color: 'var(--color-text-primary)', mt: 0.5, fontVariantNumeric: 'tabular-nums' }}>
                  {categoryData.length}
                </Typography>
              </Box>
            </Grid>
          </Grid>

          {/* Monthly Revenue Evolution Chart */}
          {monthlyTrends.length > 0 && (
            <Paper ref={chartContainerRef} sx={{ p: 3, mb: 3, border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)' }}>
              <Typography sx={{ fontSize: 'var(--text-lg)', fontWeight: 600, color: 'var(--color-text-primary)', fontFamily: 'var(--font-heading)', mb: 2 }}>
                Monthly Revenue Evolution by Category
              </Typography>
              <ResponsiveContainer width="100%" height={380}>
                <BarChart data={monthlyTrends} margin={{ left: 8, right: 8, top: 10 }} barCategoryGap="18%" barGap={2}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#EFEFEA" vertical={false} />
                  <XAxis
                    dataKey="month"
                    tick={{ fontSize: 11, fill: '#8A8A85' }}
                    tickLine={false}
                    axisLine={{ stroke: '#E5E5E0' }}
                  />
                  <YAxis
                    tick={{ fontSize: 10, fill: '#8A8A85' }}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(v: number) => v >= 1000000 ? `${(v / 1000000).toFixed(1)}M` : v >= 1000 ? `${(v / 1000).toFixed(0)}K` : `${v}`}
                  />
                  <Tooltip
                    contentStyle={{ background: '#FDFBF7', border: '1px solid #E5E5E0', borderRadius: 8, boxShadow: '0 2px 8px rgba(114,47,55,0.10)', fontSize: 12 }}
                    formatter={(value: number) => [fmtCurrencyFull(value), undefined]}
                  />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  {categories.map((cat, idx) => (
                    <Bar
                      key={cat}
                      dataKey={cat}
                      name={cat}
                      fill={CATEGORY_COLORS[idx % CATEGORY_COLORS.length]}
                      radius={[3, 3, 0, 0]}
                      stackId="revenue"
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          )}

          {/* Category Performance Table */}
          <TableContainer component={Paper} sx={{ mb: 3, border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ '& .MuiTableCell-head': { fontSize: '11px', fontWeight: 700, py: '6px', px: '10px', whiteSpace: 'nowrap' } }}>
                  <TableCell sx={{ minWidth: 110 }}>Category</TableCell>
                  <TableCell align="right" sx={{ width: 60 }}>Products</TableCell>
                  <TableCell align="right" sx={{ width: 80 }}>Units Sold</TableCell>
                  <TableCell align="right" sx={{ width: 100 }}>Revenue</TableCell>
                  <TableCell align="right" sx={{ width: 90 }}>COGS</TableCell>
                  <TableCell align="right" sx={{ width: 76 }}>Margin</TableCell>
                  <TableCell align="right" sx={{ width: 90 }}>Avg Txn</TableCell>
                  <TableCell align="right" sx={{ width: 110 }}>Revenue Share</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {categoryData.map((item, idx) => (
                  <TableRow key={idx} hover sx={{ '& .MuiTableCell-body': { fontSize: '11px', py: '5px', px: '10px' } }}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: CATEGORY_COLORS[idx % CATEGORY_COLORS.length], flexShrink: 0 }} />
                        <Typography sx={{ fontWeight: 600, fontSize: '11px' }}>{item.category}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right">{item.product_count}</TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{item.total_units_sold.toLocaleString()}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{fmtCurrencyFull(item.total_revenue)}</TableCell>
                    <TableCell align="right" sx={{ color: 'var(--color-text-muted)', fontVariantNumeric: 'tabular-nums' }}>{fmtCurrencyFull(item.total_cogs)}</TableCell>
                    <TableCell align="right">
                      <Chip
                        label={`${item.gross_margin_pct.toFixed(1)}%`}
                        size="small"
                        sx={{
                          bgcolor: item.gross_margin_pct >= 60 ? 'var(--color-success-bg)' : item.gross_margin_pct >= 40 ? '#FFF9C4' : 'var(--color-danger-bg)',
                          color: item.gross_margin_pct >= 60 ? 'var(--color-success)' : item.gross_margin_pct >= 40 ? '#F57F17' : 'var(--color-danger)',
                          fontWeight: 700,
                          fontSize: '10px',
                          height: 20,
                        }}
                      />
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{fmtCurrencyFull(item.avg_transaction_value)}</TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.75 }}>
                        <Box sx={{ width: 50, height: 5, bgcolor: '#F5F5F0', borderRadius: 3, overflow: 'hidden' }}>
                          <Box sx={{ width: `${Math.min(item.revenue_share_percent, 100)}%`, height: '100%', bgcolor: CATEGORY_COLORS[idx % CATEGORY_COLORS.length], borderRadius: 3 }} />
                        </Box>
                        <Typography sx={{ fontSize: '10px', fontWeight: 600, minWidth: 32, textAlign: 'right' }}>
                          {item.revenue_share_percent.toFixed(1)}%
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
                {/* Total row */}
                <TableRow sx={{ bgcolor: 'var(--color-surface)', '& .MuiTableCell-body': { fontSize: '11px', py: '5px', px: '10px' } }}>
                  <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>{categoryData.reduce((s, c) => s + c.product_count, 0)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{categoryData.reduce((s, c) => s + c.total_units_sold, 0).toLocaleString()}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{fmtCurrencyFull(categoryData.reduce((s, c) => s + c.total_revenue, 0))}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{fmtCurrencyFull(categoryData.reduce((s, c) => s + c.total_cogs, 0))}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    {(() => {
                      const totRev = categoryData.reduce((s, c) => s + c.total_revenue, 0);
                      const totCogs = categoryData.reduce((s, c) => s + c.total_cogs, 0);
                      return totRev > 0 ? ((totRev - totCogs) / totRev * 100).toFixed(1) + '%' : '-';
                    })()}
                  </TableCell>
                  <TableCell align="right" />
                  <TableCell align="right" sx={{ fontWeight: 700 }}>100%</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          {/* AI-Powered 4-Part Business Analysis */}
          {aiAnalysis && (
            <Paper sx={{ p: 3, border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', background: 'linear-gradient(135deg, #FDFBF7 0%, #F8F4EF 100%)' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5 }}>
                <AIIcon sx={{ color: '#722F37', fontSize: 22 }} />
                <Typography sx={{ fontSize: 'var(--text-lg)', fontWeight: 600, color: 'var(--color-text-primary)', fontFamily: 'var(--font-heading)' }}>
                  AI-Powered Business Analysis
                </Typography>
                <Chip label="Auto-generated" size="small" sx={{ bgcolor: '#722F37', color: 'white', fontSize: 10, height: 20, fontWeight: 600 }} />
              </Box>

              {/* Snapshot */}
              {aiAnalysis.snapshot && (
                <Box sx={{ mb: 2.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <SnapshotIcon sx={{ color: '#8B6F8B', fontSize: 18 }} />
                    <Typography sx={{ fontSize: 'var(--text-base)', fontWeight: 700, color: 'var(--color-text-primary)' }}>
                      Snapshot
                    </Typography>
                  </Box>
                  <Box sx={{ p: 1.5, bgcolor: 'rgba(255,255,255,0.7)', borderRadius: 'var(--radius-sm)', border: '1px solid rgba(0,0,0,0.04)' }}>
                    <Typography sx={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)', lineHeight: 1.6 }}>
                      {aiAnalysis.snapshot}
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* Risks */}
              {aiAnalysis.risks && aiAnalysis.risks.length > 0 && (
                <Box sx={{ mb: 2.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <WarningIcon sx={{ color: 'var(--color-danger)', fontSize: 18 }} />
                    <Typography sx={{ fontSize: 'var(--text-base)', fontWeight: 700, color: 'var(--color-text-primary)' }}>
                      Risks
                    </Typography>
                    <Chip label={`${aiAnalysis.risks.length}`} size="small" color="error" sx={{ height: 20, fontSize: 10, fontWeight: 700 }} />
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {aiAnalysis.risks.map((risk, idx) => (
                      <Box
                        key={idx}
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 1.5,
                          p: 1.5,
                          bgcolor: 'rgba(255,255,255,0.7)',
                          borderRadius: 'var(--radius-sm)',
                          border: '1px solid rgba(0,0,0,0.04)',
                          borderLeft: '3px solid var(--color-danger)',
                        }}
                      >
                        <Typography sx={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)', lineHeight: 1.5, flex: 1 }}>
                          {risk}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}

              {/* Opportunities */}
              {aiAnalysis.opportunities && aiAnalysis.opportunities.length > 0 && (
                <Box sx={{ mb: 2.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <LightbulbIcon sx={{ color: '#C9A96E', fontSize: 18 }} />
                    <Typography sx={{ fontSize: 'var(--text-base)', fontWeight: 700, color: 'var(--color-text-primary)' }}>
                      Opportunities
                    </Typography>
                    <Chip label={`${aiAnalysis.opportunities.length}`} size="small" color="warning" sx={{ height: 20, fontSize: 10, fontWeight: 700 }} />
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {aiAnalysis.opportunities.map((opp, idx) => (
                      <Box
                        key={idx}
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 1.5,
                          p: 1.5,
                          bgcolor: 'rgba(255,255,255,0.7)',
                          borderRadius: 'var(--radius-sm)',
                          border: '1px solid rgba(0,0,0,0.04)',
                          borderLeft: '3px solid #C9A96E',
                        }}
                      >
                        <Typography sx={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)', lineHeight: 1.5, flex: 1 }}>
                          {opp}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}

              {/* Suggested Action Plan */}
              {aiAnalysis.action_plan && aiAnalysis.action_plan.length > 0 && (
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <ActionIcon sx={{ color: 'var(--color-success)', fontSize: 18 }} />
                    <Typography sx={{ fontSize: 'var(--text-base)', fontWeight: 700, color: 'var(--color-text-primary)' }}>
                      Suggested Action Plan
                    </Typography>
                    <Chip label={`${aiAnalysis.action_plan.length}`} size="small" color="success" sx={{ height: 20, fontSize: 10, fontWeight: 700 }} />
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {aiAnalysis.action_plan.map((action, idx) => (
                      <Box
                        key={idx}
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 1.5,
                          p: 1.5,
                          bgcolor: 'rgba(255,255,255,0.7)',
                          borderRadius: 'var(--radius-sm)',
                          border: '1px solid rgba(0,0,0,0.04)',
                          borderLeft: '3px solid var(--color-success)',
                        }}
                      >
                        <Box sx={{ minWidth: 20, height: 20, borderRadius: '50%', bgcolor: 'var(--color-success)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, flexShrink: 0, mt: 0.2 }}>
                          {idx + 1}
                        </Box>
                        <Typography sx={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)', lineHeight: 1.5, flex: 1 }}>
                          {action}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
            </Paper>
          )}

          {/* Multi-Dimensional Insights — brand / category / geographic / mix */}
          {dimensional && (
            (dimensional.brand_insights?.length ||
             dimensional.category_insights?.length ||
             dimensional.geographic_insights?.length ||
             dimensional.mix_insights?.length) ? (
              <Paper
                sx={{
                  p: 3,
                  mt: 3,
                  border: '1px solid var(--color-border)',
                  borderRadius: 'var(--radius-md)',
                  background: 'linear-gradient(135deg, #FDFBF7 0%, #F5EFE6 100%)',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5 }}>
                  <AIIcon sx={{ color: '#722F37', fontSize: 22 }} />
                  <Typography
                    sx={{
                      fontSize: 'var(--text-lg)',
                      fontWeight: 600,
                      color: 'var(--color-text-primary)',
                      fontFamily: 'var(--font-heading)',
                    }}
                  >
                    Multi-Dimensional Insights
                  </Typography>
                  <Chip
                    label="Brand · Category · Geography · Mix"
                    size="small"
                    sx={{ bgcolor: '#8B6F8B', color: 'white', fontSize: 10, height: 20, fontWeight: 600 }}
                  />
                </Box>

                <Grid container spacing={2}>
                  {/* Brand */}
                  {dimensional.brand_insights && dimensional.brand_insights.length > 0 && (
                    <Grid item xs={12} md={6}>
                      <Box
                        sx={{
                          p: 2,
                          height: '100%',
                          bgcolor: 'rgba(255,255,255,0.85)',
                          borderRadius: 'var(--radius-sm)',
                          border: '1px solid rgba(0,0,0,0.05)',
                          borderTop: '3px solid #722F37',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                          <BrandIcon sx={{ color: '#722F37', fontSize: 18 }} />
                          <Typography sx={{ fontSize: 'var(--text-sm)', fontWeight: 700, color: 'var(--color-text-primary)' }}>
                            Brandwise
                          </Typography>
                          <Chip
                            label={`${dimensional.brand_insights.length}`}
                            size="small"
                            sx={{ height: 18, fontSize: 10, fontWeight: 700, bgcolor: '#722F37', color: 'white' }}
                          />
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          {dimensional.brand_insights.map((txt, i) => (
                            <Typography
                              key={i}
                              sx={{
                                fontSize: 'var(--text-xs)',
                                color: 'var(--color-text-secondary)',
                                lineHeight: 1.55,
                                pl: 1.2,
                                position: 'relative',
                                '&::before': {
                                  content: '"•"',
                                  position: 'absolute',
                                  left: 0,
                                  color: '#722F37',
                                  fontWeight: 700,
                                },
                              }}
                            >
                              {renderRich(txt)}
                            </Typography>
                          ))}
                        </Box>
                      </Box>
                    </Grid>
                  )}

                  {/* Category */}
                  {dimensional.category_insights && dimensional.category_insights.length > 0 && (
                    <Grid item xs={12} md={6}>
                      <Box
                        sx={{
                          p: 2,
                          height: '100%',
                          bgcolor: 'rgba(255,255,255,0.85)',
                          borderRadius: 'var(--radius-sm)',
                          border: '1px solid rgba(0,0,0,0.05)',
                          borderTop: '3px solid #C9A96E',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                          <CategoryIcon sx={{ color: '#C9A96E', fontSize: 18 }} />
                          <Typography sx={{ fontSize: 'var(--text-sm)', fontWeight: 700, color: 'var(--color-text-primary)' }}>
                            Categorywise
                          </Typography>
                          <Chip
                            label={`${dimensional.category_insights.length}`}
                            size="small"
                            sx={{ height: 18, fontSize: 10, fontWeight: 700, bgcolor: '#C9A96E', color: 'white' }}
                          />
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          {dimensional.category_insights.map((txt, i) => (
                            <Typography
                              key={i}
                              sx={{
                                fontSize: 'var(--text-xs)',
                                color: 'var(--color-text-secondary)',
                                lineHeight: 1.55,
                                pl: 1.2,
                                position: 'relative',
                                '&::before': {
                                  content: '"•"',
                                  position: 'absolute',
                                  left: 0,
                                  color: '#C9A96E',
                                  fontWeight: 700,
                                },
                              }}
                            >
                              {renderRich(txt)}
                            </Typography>
                          ))}
                        </Box>
                      </Box>
                    </Grid>
                  )}

                  {/* Geographic */}
                  {dimensional.geographic_insights && dimensional.geographic_insights.length > 0 && (
                    <Grid item xs={12} md={6}>
                      <Box
                        sx={{
                          p: 2,
                          height: '100%',
                          bgcolor: 'rgba(255,255,255,0.85)',
                          borderRadius: 'var(--radius-sm)',
                          border: '1px solid rgba(0,0,0,0.05)',
                          borderTop: '3px solid #6B8E7B',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                          <GeoIcon sx={{ color: '#6B8E7B', fontSize: 18 }} />
                          <Typography sx={{ fontSize: 'var(--text-sm)', fontWeight: 700, color: 'var(--color-text-primary)' }}>
                            Geographic
                          </Typography>
                          <Chip
                            label={`${dimensional.geographic_insights.length}`}
                            size="small"
                            sx={{ height: 18, fontSize: 10, fontWeight: 700, bgcolor: '#6B8E7B', color: 'white' }}
                          />
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          {dimensional.geographic_insights.map((txt, i) => (
                            <Typography
                              key={i}
                              sx={{
                                fontSize: 'var(--text-xs)',
                                color: 'var(--color-text-secondary)',
                                lineHeight: 1.55,
                                pl: 1.2,
                                position: 'relative',
                                '&::before': {
                                  content: '"•"',
                                  position: 'absolute',
                                  left: 0,
                                  color: '#6B8E7B',
                                  fontWeight: 700,
                                },
                              }}
                            >
                              {renderRich(txt)}
                            </Typography>
                          ))}
                        </Box>
                      </Box>
                    </Grid>
                  )}

                  {/* Channel / Format mix */}
                  {dimensional.mix_insights && dimensional.mix_insights.length > 0 && (
                    <Grid item xs={12} md={6}>
                      <Box
                        sx={{
                          p: 2,
                          height: '100%',
                          bgcolor: 'rgba(255,255,255,0.85)',
                          borderRadius: 'var(--radius-sm)',
                          border: '1px solid rgba(0,0,0,0.05)',
                          borderTop: '3px solid #8B6F8B',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                          <MixIcon sx={{ color: '#8B6F8B', fontSize: 18 }} />
                          <Typography sx={{ fontSize: 'var(--text-sm)', fontWeight: 700, color: 'var(--color-text-primary)' }}>
                            Channel & Mix
                          </Typography>
                          <Chip
                            label={`${dimensional.mix_insights.length}`}
                            size="small"
                            sx={{ height: 18, fontSize: 10, fontWeight: 700, bgcolor: '#8B6F8B', color: 'white' }}
                          />
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          {dimensional.mix_insights.map((txt, i) => (
                            <Typography
                              key={i}
                              sx={{
                                fontSize: 'var(--text-xs)',
                                color: 'var(--color-text-secondary)',
                                lineHeight: 1.55,
                                pl: 1.2,
                                position: 'relative',
                                '&::before': {
                                  content: '"•"',
                                  position: 'absolute',
                                  left: 0,
                                  color: '#8B6F8B',
                                  fontWeight: 700,
                                },
                              }}
                            >
                              {renderRich(txt)}
                            </Typography>
                          ))}
                        </Box>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </Paper>
            ) : null
          )}
        </>
      ) : !isLoading && !isError ? (
        <Box sx={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', padding: '48px 20px', textAlign: 'center', background: 'var(--color-surface)' }}>
          <Typography sx={{ fontSize: 'var(--text-base)', fontWeight: 600, color: 'var(--color-text-secondary)', mb: 0.5 }}>
            No Data Available
          </Typography>
          <Typography sx={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
            Category performance data will appear here once inventory and sales records are populated.
          </Typography>
        </Box>
      ) : null}
    </Box>
  );
};

export default CategoryPerformance;
